import React, { useCallback, useState } from "react";
import { IResultConfig } from "@piceasoft/core";
import { strings } from "../../../../../localization/strings";
import { Section } from "../../../decorations/Section";
import { ParameterItem } from "../../components/ParameterItem";
import { Dropdown, Icon, IDropdownOption, IDropdownStyles, IIconProps, IRenderFunction, ISelectableOption, SharedColors, Stack, Text } from '@fluentui/react'
import { useDispatch, useSelector } from "react-redux";
import { IStore } from "../../../../../core/store";
import { actionCreators } from "../../../../../core/actions/workflow-actions";
import { StructureNodeTypes } from "../../../../../screens/organization/OrganizationStructure/Elements/StructureNodeTypes";
import { IStructureNode } from "../../../../../core/store/typings/IStructureNode";

type TProps = {
    config: IResultConfig
    onConfigCommit: (config: IResultConfig) => void
    channel: number
}

const zoomLevelOptions: IDropdownOption[] = [
    // { key: 0, text: "0" },
    { key: 1, text: "1" },
    { key: 2, text: "2" },
    { key: 3, text: "3" },
    { key: 4, text: "4" },
    { key: 5, text: "5" },
    { key: 6, text: "6" },
    { key: 7, text: "7" },
    { key: 8, text: "8" },
    { key: 9, text: "9" },
    { key: 10, text: "10" },
    { key: 11, text: "11" },
    { key: 12, text: "12" },
    { key: 13, text: "13" },
    { key: 14, text: "14" },
    { key: 15, text: "15" },
    { key: 16, text: "16" },
    { key: 17, text: "17" },
    { key: 18, text: "18" }
]

interface IFolderTreeNode
{
    id: string,
    nestingLevel: number,
    children: string[],
    type: StructureNodeTypes
    name: string
}

const countNesting = ( data: IStructureNode[], id: string, level: number): number => {
    const t = data.find( s => s.id === id)
    if( t && t.parentId) {
        return countNesting( data, t.parentId, level +1);
    }
    return level
}

const getNodeIconProps = (type: StructureNodeTypes): IIconProps => {
    switch (type) {
        case StructureNodeTypes.Container: return { iconName: "FabricFolderFill", style: { fontSize: 16, color: '#dcb67a' } }
        case StructureNodeTypes.Point: return { iconName: "Home", style: { fontSize: 16 } }
        case StructureNodeTypes.Person: return { iconName: "Contact", style: { fontSize: 16 } }
        default: return { iconName: "Document", style: { fontSize: 16 } }
    }
}

export const ResultMapPivot: React.FC<TProps> = (props) => {
    const dispatch = useDispatch()
    const commitChange = props.onConfigCommit
    const mapEnabled = props.config.ui?.map?.enabled
    const structure = useSelector((s: IStore) => s.workflow.structure)
    const [folderOptions, setFolderOptions] = useState<IDropdownOption<IFolderTreeNode>[]>( [])
    const selectedKeys = useSelector((s:IStore) => s.configurator.result?.ui?.map?.storeFolders ?? [])

    React.useEffect(() => {
        dispatch(actionCreators.requestStructure())
    }, [dispatch])

    React.useEffect(() => {
        if( !structure.isReady) return;

        const topChildren: string[] = []
        const containers = structure.data.filter( s => s.type === StructureNodeTypes.Container)
        let tmp2 = containers.map( t => {
            const children = containers.filter( s => s.parentId === t.id)?.map( ss => ss.id) ?? []
            const nestingLevel = countNesting( containers, t.id, 1);
            if( nestingLevel === 1) {
                topChildren.push( t.id);
            }
            return {
                id: t.id,
                nestingLevel,
                children,
                name: t.itemName,
                type: t.type
            } as IFolderTreeNode
        });

        const options: IDropdownOption<IFolderTreeNode>[]  = []
        const addChilden = (children: string[]) => {
            children.forEach( c => {
                const t = tmp2.find( tt => tt.id == c)
                if( t) {
                    options.push( {
                        key: t.id,
                        text: t.name,
                        data: t,
                    })
                    if( t.children.length > 0)
                        addChilden( t.children)
                }
            })
        }

        //  Shops can exist at top level too, so need a fake root folder for selection.
        const root = {
            id: '{00000000-0000-0000-0000-000000000000}',
            nestingLevel: 0,
            children: topChildren,
            name: strings.NAVIGATION.ORGANIZATION.TITLE,
            type: StructureNodeTypes.Container,
        }
        tmp2.push( root)
        tmp2 = tmp2.sort( (l, r) => l.nestingLevel - r.nestingLevel)
        options.push( {
            key: root.id,
            text: root.name,
            data: root,
        })
        if( root.children.length > 0)
            addChilden( root.children);

        setFolderOptions(options)
    }, [structure])

    const onRenderFolderOption: IRenderFunction<ISelectableOption<IFolderTreeNode>> = (item) => {
        const leftMargin = (item?.data?.nestingLevel ?? 1) * 22;
        const iconProps = getNodeIconProps( item?.data?.type ?? StructureNodeTypes.Container)
        return (
            <Stack verticalAlign='center' horizontal grow tokens={{ padding: 4, childrenGap: 6 }}
                style={{marginLeft: leftMargin}}
            >
                <Stack verticalAlign='center' style={{ height: 24 }}>
                    <Icon {...iconProps}/>
                </Stack>
                <Stack tokens={{ padding: 2 }}>
                    <Text block nowrap variant="medium" style={{ color: SharedColors.gray20 }}>{item?.text}</Text>
                </Stack>
            </Stack>
        );
    }

    const onChange = useCallback( (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption<IFolderTreeNode>): void => {
        if( !item || !item.data) return;

        let folders = selectedKeys.slice();
        if( folders.includes( item.data.id)) 
            folders = folders.filter( f => f !== item.data?.id)
        else {
            folders.push( item.data.id)
        }

        commitChange({
            ...props.config,
            ui: {
                ...props.config.ui,
                map: {
                    ...props.config.ui?.map,
                    storeFolders: folders
                }
            }
        })
    }, [props.config, selectedKeys, commitChange]);

    return (
        <Section max flat>
         <ParameterItem title={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.MAP.TITLE_TITLE}
                useToggle
                noSeparator
                toggleEnabled={true}
                toggleChecked={props.config.ui?.map?.enabled === undefined  ||  props.config.ui?.map?.enabled === false ? false : true}
                toggleOnChange={() => {
                    props.onConfigCommit({
                        ...props.config,
                        ui: {
                            ...props.config.ui,
                            map: {
                                ...props.config.ui?.map,
                                enabled: props.config.ui?.map?.enabled === undefined  ||  props.config.ui?.map?.enabled === false ? true : false
                            }
                        }
                    })
                }}
                description={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.MAP.TITLE_DESCRIPTION}
            >
                { mapEnabled && 
                (<Stack >
                    <Dropdown options={folderOptions}
                        label={strings.	CONSTRUCTOR.STAGES.RESULT.PIVOTS.MAP.FOLDERS.TITLE}
                        multiSelect
                        onChange={onChange}
                        selectedKeys={selectedKeys}
                        onRenderOption={onRenderFolderOption}
                        styles={dropdownStyles}
                    />
                    <Text>{strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.MAP.FOLDERS.DESCRIPTION}</Text>
                </Stack>)}
            </ParameterItem>
        <ParameterItem 
            title={strings.CONSTRUCTOR.STAGES.RESULT.PIVOTS.MAP.BUTTON_DESCRIPTION}
            useToggle
            toggleEnabled={props.config.ui?.map?.enabled ? true : false}
            toggleChecked={props.config.ui?.map?.button !== undefined ? true : false}
            toggleOnChange={() => {
                props.onConfigCommit({
                    ...props.config,
                    ui:{
                        ...props.config.ui,
                        map: {
                            ...props.config.ui?.map,
                            button: props.config.ui?.map?.button !== undefined ? undefined : ""
                        }
                    }
                })
            }}
            useTextField
            textFieldEnabled={props.config.ui?.map?.button !== undefined ? true : false}
            textFieldValue={props.config.ui?.map?.button}
            textFieldOnChange={(val) => props.onConfigCommit({
                ...props.config,
                ui:{
                    ...props.config.ui,
                    map:{
                        ...props.config.ui?.map,
                        button: val
                    }
                }
            })}
            description={strings.CONSTRUCTOR.STAGES.COMMON.PIVOTS.UI.BUTTONS.NEXT.TEXT_DESCRIPTION}
        />

            {/* {props.config.ui?.map?.enabled === true && (
                <ParameterItem
                    title={'Use custom zoom level'}
                    useToggle
                    toggleEnabled={true}
                    toggleChecked={true}
                    useDropdown={true}
                    dropdownEnabled={props.config.ui?.map?.enabled === true}
                    dropdownSelectedKey={props.config.ui?.map?.zoomLevel || 10}
                    dropdownOptions={zoomLevelOptions}
                    dropdownOnChange={(opt) => props.onConfigCommit({
                        ...props.config,
                        ui: {
                            ...props.config.ui,
                            map: {
                                ...props.config.ui?.map,
                                zoomLevel: opt?.key as number
                            }
                        }
                    })
                    }
                    description={'Use custom zoom level'}
                    // errorMessage={'plaplapla'}
                />)} */}
        </Section>
    )
}

const dropdownStyles: Partial<IDropdownStyles> = { dropdown: { width: 300 } };